import type { SortingState } from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/locales/i18n-provider";

export type DataTableSortOption = {
  label: string;
  value: string;
};

type DataTableSortListProps = {
  maxSorts?: number;
  options: ReadonlyArray<DataTableSortOption>;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
};

/** Dice UI 的多字段排序面板；排序规则由页面持有并直接驱动服务端查询。 */
export function DataTableSortList({ maxSorts = 3, options, sorting, onSortingChange }: DataTableSortListProps) {
  const { intlLocale, t } = useI18n();
  const numberFormatter = new Intl.NumberFormat(intlLocale);
  const availableOptions = options.filter((option) => !sorting.some((sort) => sort.id === option.value));

  const updateRule = (index: number, patch: Partial<SortingState[number]>) => {
    onSortingChange(sorting.map((sort, sortIndex) => (sortIndex === index ? { ...sort, ...patch } : sort)));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="lg" variant="outline">
          <ArrowUpDownIcon data-icon="inline-start" />
          {t("dataTable.sort.trigger")}
          {sorting.length > 0 ? (
            <Badge className="min-w-5 rounded-sm px-1 font-normal" variant="secondary">
              {numberFormatter.format(sorting.length)}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 sm:w-96">
        <PopoverHeader>
          <PopoverTitle>{t("dataTable.sort.title")}</PopoverTitle>
          <PopoverDescription>{t("dataTable.sort.description", { count: numberFormatter.format(maxSorts) })}</PopoverDescription>
        </PopoverHeader>

        <div className="flex flex-col gap-2">
          {sorting.length > 0 ? (
            sorting.map((sort, index) => (
              <div className="flex items-center gap-2" key={sort.id}>
                <Select value={sort.id} onValueChange={(value) => updateRule(index, { id: value })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {options.map((option) => {
                        const usedElsewhere = sorting.some((item, sortIndex) => sortIndex !== index && item.id === option.value);
                        return (
                          <SelectItem disabled={usedElsewhere} key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <Select value={sort.desc ? "desc" : "asc"} onValueChange={(value) => updateRule(index, { desc: value === "desc" })}>
                  <SelectTrigger className="w-28 shrink-0">
                    {sort.desc ? <ArrowDownIcon /> : <ArrowUpIcon />}
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="asc">{t("dataTable.sort.ascending")}</SelectItem>
                      <SelectItem value="desc">{t("dataTable.sort.descending")}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <Button
                  aria-label={t("dataTable.sort.remove", { index: numberFormatter.format(index + 1) })}
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => onSortingChange(sorting.filter((_, sortIndex) => sortIndex !== index))}
                >
                  <Trash2Icon />
                </Button>
              </div>
            ))
          ) : (
            <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">{t("dataTable.sort.empty")}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={sorting.length >= maxSorts || availableOptions.length === 0}
            size="sm"
            variant="outline"
            onClick={() => {
              const option = availableOptions[0];
              if (option) onSortingChange([...sorting, { id: option.value, desc: false }]);
            }}
          >
            <PlusIcon data-icon="inline-start" />
            {t("dataTable.sort.add")}
          </Button>
          {sorting.length > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => onSortingChange([])}>
              {t("dataTable.sort.clear")}
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
